import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  PaymentDiscountFormData,
  PaymentDiscountSchema,
  PaymentModeFormData,
  PaymentModeSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import {
  PaymentDiscountData,
  PaymentModeData,
} from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";

const PaymentModeForm: React.FC = () => {
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("modal-item");
  const modalMode = searchParams.get("modal");

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PaymentModeFormData>({
    mode: "onChange",
    resolver: zodResolver(PaymentModeSchema),
  });
  console.log("Error", errors);
  /* set default value */

  /* set default value */
  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<PaymentModeData>
  >({
    endpoint: endpoint["paymentmode"],
    key: `${pathname}-${modalItem}`,
    enabled: modalItem ? true : false,
    id: modalItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      if (firstResult) {
        if (modalMode == "add") {
          return reset({});
        } else {
          reset(() => ({
            name: firstResult?.name || "",
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const handleMutate = (data: any) => {
    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
    }
    return {
      data,
      endpoint: endpoint["paymentmode"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: PaymentModeData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      showToast({
        description: `Payment ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };
  const { mutate, isPending } = usePostData();
  const onSubmit = (data: PaymentModeFormData) => {
    console.log("data", data);

    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "name",
      label: "Mode Name",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Mode Name",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "submit",
      value: `${searchParams.get("modal") || ""} ${(type || pathname)?.replace(
        "-",
        " "
      )}`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 12,
      variant: "default",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
  ];

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {" "}
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

const PaymentDiscountForm: React.FC = () => {
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("modal-item");
  const modalMode = searchParams.get("modal");
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PaymentDiscountFormData>({
    mode: "onChange",
    resolver: zodResolver(PaymentDiscountSchema),
  });

  console.log("Error", errors);

  /* set default value */
  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<PaymentDiscountFormData & { discount_type: string }>
  >({
    endpoint: endpoint["discount"],
    key: `${pathname}-${modalItem}`,
    enabled: modalItem ? true : false,
    id: modalItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      if (firstResult) {
        if (modalMode == "add") {
          return reset({});
        } else {
          //@ts-ignore
          reset(() => ({
            ...firstResult,
            name: firstResult.name,
            //@ts-ignore
            discount_type: [
              {
                label:
                  firstResult.discount_type === "percent" ? "Percent" : "Flat",
                value: firstResult.discount_type,
              },
            ],
            limit: firstResult.limit,
            value: firstResult.value,
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const handleMutate = (data: any) => {
    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
    }
    data = { ...data, discount_type: data["discount_type"][0]["value"] };
    return {
      data,
      endpoint: endpoint["discount"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: PaymentDiscountData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      showToast({
        description: `Payment ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);
    const errorMessage = error.data.response.data;

    throwErrorAlert(errorMessage);
  };
  const { mutate, isPending } = usePostData();
  const onSubmit = (data: PaymentDiscountFormData) => {
    console.log("data", data);

    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "name",
      label: "Coupon Name",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Name",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "discount_type",
      label: "Discount Type",
      colSpan: 6,
      type: "async",
      option: [
        { label: "Percent", value: "percent" },
        { label: "Flat", value: "flat" },
      ],
      placeholder: "Enter Discount Type",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "limit",
      label: "Limit",
      colSpan: 6,
      type: "text",
      placeholder: "Enter limit",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "value",
      label: "Discount Amount",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Value",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "discount_coupon",
      label: "Coupon Code",
      colSpan: 12,
      type: "text",
      placeholder: "Enter coupon",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      type: "submit",
      value: `${searchParams.get("modal") || ""} ${(type || pathname)?.replace(
        /-/g,
        " "
      )}`,
      className: "w-full h-[47px] mt-[32px] capitalize",
      colSpan: 12,
      variant: "default",
      endIcon: isPending ? <Spinner /> : null,
      disabled: isPending ? true : false,
      visable: true,
    },
  ];

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default PaymentModeForm;
export { PaymentDiscountForm };
